import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePlatformFeatures, type PlatformFeatureKey } from '../../shared/hooks/usePlatformFeatures';

interface FeatureRouteProps {
  featureKey: PlatformFeatureKey;
  /** Where to send users when the feature is disabled. Defaults to the
   *  nearest dashboard root inferred from the current path. */
  redirectTo?: string;
}

function inferDashboardRoot(pathname: string): string {
  if (pathname.startsWith('/referral-dashboard/jobseeker-dashboard')) {
    return '/referral-dashboard/jobseeker-dashboard';
  }
  if (pathname.startsWith('/referral-dashboard')) {
    return '/referral-dashboard';
  }
  return '/jobseeker-dashboard';
}

/**
 * Wraps a route group: if the feature is disabled, redirects to the
 * dashboard root.  While the feature list is still loading, renders
 * children (fail-open) to avoid a flash of redirect.
 */
export function FeatureRoute({ featureKey, redirectTo }: FeatureRouteProps) {
  const { isFeatureEnabled, loading } = usePlatformFeatures();
  const location = useLocation();

  if (loading) return <Outlet />;

  if (!isFeatureEnabled(featureKey)) {
    const target = redirectTo ?? inferDashboardRoot(location.pathname);
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}
